import { PropsWithChildren } from 'react';
import { Dialog } from '@mui/material';
import { DraggablePaper, SlideTransition } from '../../Infrastructure/Theme/AppThemeComponents';
import { useAppDispatch } from '@reduxStore';
import { removeModal } from './modalSlice';
import { useModalProvider } from './ModalContext';
import classNames from 'classnames/bind';

import styles from './ApplicationModal.module.css';
const classCtx = classNames.bind(styles);

interface FormikModalProps {
	/** Flag whether the modal is open or closed */
	isOpen: boolean;
	/** Handler for closing the modal */
	onClose: () => void;
	/** Handler after the modal has left the screen */
	onTransitionExited?: () => void;
	/** CSS Classname to be applied to the underlying paper component. Used for controlling dimensions, etc. */
	paperClass?: string;
	/** Allows the consuming component to specify the modal should take up the entire screen */
	fullScreenOverride?: boolean;
	/** */
	width?: string;
}

/**
 * Dialog wrapper to implement Application preferences and behavior.
 * Children component should be wrapped in a MUI <DialogContent> component.
 * Action buttons should be wrapped in a MUI <DialogActions> component.
 */
export default function FormikModal(props: PropsWithChildren & FormikModalProps) {
	const { isOpen, onClose } = props;
	const appDispatch = useAppDispatch();
	const modalContext = useModalProvider();

	/** Handler event for closing the modal */
	const handleClose = (_: object, reason?: 'backdropClick' | 'escapeKeyDown') => {
		if (reason && reason === 'backdropClick') {
			return;
		}

		onClose();
	};

	const handleTransitionExit = () => {
		appDispatch(removeModal());
		modalContext.reset();
		if (props.onTransitionExited) {
			props.onTransitionExited();
		}
	};

	const paperClasses = classCtx({
		paper: true,
		[props.paperClass ?? '']: Boolean(props.paperClass)
	});

	return (
		<Dialog
			open={isOpen}
			onClose={handleClose}
			slots={{
				paper: DraggablePaper,
				transition: SlideTransition
			}}
			slotProps={{
				paper: {
					className: paperClasses,
					fullScreenOverride: props.fullScreenOverride
				}
			}}
			onTransitionExited={handleTransitionExit}
			aria-labelledby={'draggable-window'}
			fullScreen={props.fullScreenOverride}
			sx={{
				'& .MuiPaper-root': {
					width: props.width
				}
			}}
		>
			{props.children}
		</Dialog>
	);
}
